import { BP_DEFENSE, BP_FAMILLE, BP_HOMMES, BP_MURS, BP_PHOTO, BP_SORTIES } from "./content2";
import { DataTable, SectionShell, StatGrid } from "./blocks";
import { BusinessPlanSuite } from "./BusinessPlanSuite";
import { useInView } from "./useInView";

const Intro = () => (
  <div className="grid lg:grid-cols-[1fr_1fr] gap-12">
    <div>
      <h2 className="disp mm-h2">Business Plan Entreprise France · plan à 10 ans</h2>
      <p className="disp text-2xl text-mm-red mt-6">« Si une famille, une entreprise ou un pays dépense plus qu'il ne gagne, combien de temps peut-il continuer ainsi ? »</p>
      <p className="mm-lab mt-4">Premier draft de travail · octobre 2026</p>
    </div>
    <div className="mm-body space-y-3 border-l-4 border-mm-gold pl-6">
      <p className="mm-lab text-mm-golddark">Préface · Les règles avant les chiffres</p>
      <p>L'homme d'entreprise que je suis a mis cinquante ans à créer des richesses, à traverser le cours de l'Histoire, à subir le système, ses crises, ses contraintes et ses règles.</p>
      <p>Dans ma famille, même règle : on ne peut pas durablement dépenser plus que l'on gagne. On entretient ce que l'on possède. On rembourse ce que l'on doit. On investit pour préparer demain.</p>
      <p className="font-bold">Quelles sont les règles à appliquer à l'Entreprise France pour arriver au résultat ?</p>
    </div>
  </div>
);

const Sorties = () => (
  <div>
    <h3 className="disp text-3xl md:text-4xl">Entrées / sorties</h3>
    <p className="mm-body mt-3">Entrées — impôts, cotisations sociales, TVA, taxes, autres recettes publiques : <strong>1 561,6 Md€</strong>. Sorties — ce que coûte l'Entreprise France :</p>
    <ul className="st grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 mt-6 tab">
      {BP_SORTIES.map(([p, m]) => (
        <li key={p} className="flex justify-between gap-4 border-b border-mm-ink/20 py-3"><span>{p}</span><span className="font-bold">{m}</span></li>
      ))}
    </ul>
    <p className="mm-body mt-6">Différence : <strong className="text-mm-red">−152,5 Md€ en 2025</strong>. La question n'est plus seulement « combien dépense-t-on ? » mais « quelle sortie doit être financée par quelle entrée ? »</p>
  </div>
);

const Famille = () => (
  <div className="bg-mm-ink text-mm-paper p-8 md:p-10">
    <p className="mm-lab text-mm-goldlight">La dette en langage de famille</p>
    <div className="st grid grid-cols-2 lg:grid-cols-5 gap-6 mt-6 tab">
      {BP_FAMILLE.map(([b, l]) => <div key={l}><p className="disp text-3xl">{b}</p><p className="text-sm mt-1 text-mm-cream">{l}</p></div>)}
    </div>
    <p className="text-sm mt-6 text-mm-cream">Cela ne veut pas dire qu'une facture sera envoyée à chacun. Cela permet seulement de comprendre la taille du problème à l'échelle d'un budget personnel.</p>
  </div>
);

const Moyens = () => (
  <div className="space-y-12">
    <div>
      <h3 className="disp text-3xl md:text-4xl mb-6">Les hommes : qui est concerné ?</h3>
      <DataTable testid="bp-hommes-table" head={["Bloc", "Effectif", "Lecture"]} rows={BP_HOMMES} />
      <p className="text-sm mt-4">Hypothèse de travail : +5 000 € par personne et par an. 30 millions de personnes = 150 Md€/an = 1 500 Md€ sur 10 ans.</p>
    </div>
    <div>
      <h3 className="disp text-3xl md:text-4xl mb-6">Les murs : entretenir, moderniser, reconstruire</h3>
      <DataTable testid="bp-murs-table" head={["Patrimoine", "Surface", "Hypothèse", "Enveloppe annuelle"]} rows={BP_MURS} />
      <p className="text-sm mt-4">Ces surfaces ne doivent pas être additionnées sans nettoyage des périmètres : ordre de grandeur d'un programme de maintenance / rénovation sur 10 ans.</p>
    </div>
    <div>
      <h3 className="disp text-3xl md:text-4xl mb-6">Défense : le poste qu'on ne peut pas oublier</h3>
      <div className="grid sm:grid-cols-3 gap-6 tab">
        {BP_DEFENSE.map(([b, l]) => <div key={l} className="border-t-4 border-mm-ink pt-4"><p className="disp text-3xl">{b}</p><p className="text-sm mt-2">{l}</p></div>)}
      </div>
      <p className="mm-body mt-6 font-bold">Combien coûte la sécurité quand elle est prête ? Et combien coûte-t-elle quand elle ne l'est pas ?</p>
    </div>
  </div>
);

export const BusinessPlan = () => {
  const [ref, cls] = useInView(0.03);
  return (
    <SectionShell id="business-plan" label="Business Plan France" bg="bg-mm-paper" innerRef={ref} cls={cls}>
      <Intro />
      <div>
        <h3 className="disp text-3xl md:text-4xl mb-8">Entreprise France aujourd'hui</h3>
        <StatGrid items={BP_PHOTO} />
      </div>
      <Sorties />
      <Famille />
      <Moyens />
      <BusinessPlanSuite />
    </SectionShell>
  );
};
